import { useEffect, useRef, useState } from 'react';
import { useStrings } from '@/i18n/Localization';
import styles from '@/components/media.module.css';

export interface AttachmentPickerProps {
  disabled: boolean;
  /** Files chosen from disk, for photo/video and for arbitrary files. */
  onFiles: (files: File[]) => void;
  /** Section 14: the picker exposes recording as well as choosing. */
  onRecordVoice: () => void;
  onRecordVideo: () => void;
}

/**
 * The Section 14 attachment picker: photo/video, file, record voice, record
 * video.
 *
 * The two file entries use separate hidden inputs with different `accept`
 * filters rather than one input whose filter is rewritten on click. Changing
 * `accept` and then calling `click()` in the same task is honoured
 * inconsistently across browsers, and the failure mode — a photo picker that
 * offers every file on the device — is silent.
 */
export function AttachmentPicker(props: AttachmentPickerProps) {
  const { t } = useStrings();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);
  const mediaInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      firstItemRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open]);

  const pick = (input: HTMLInputElement | null) => {
    setOpen(false);
    input?.click();
  };

  const onChosen = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])];
    // Resetting the value is what lets the same file be chosen twice in a row;
    // without it the change event does not fire the second time.
    event.target.value = '';
    if (files.length > 0) {
      props.onFiles(files);
    }
  };

  return (
    <div ref={containerRef} className={styles.pickerWrapper}>
      <button
        type="button"
        className={styles.pickerButton}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('attach.open')}
        disabled={props.disabled}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <span aria-hidden="true">+</span>
      </button>

      {open ? (
        <div className={styles.pickerMenu} role="menu" aria-label={t('attach.menu')}>
          <button
            ref={firstItemRef}
            type="button"
            role="menuitem"
            className={styles.pickerItem}
            onClick={() => {
              pick(mediaInputRef.current);
            }}
          >
            {t('attach.photoVideo')}
          </button>
          <button
            type="button"
            role="menuitem"
            className={styles.pickerItem}
            onClick={() => {
              pick(fileInputRef.current);
            }}
          >
            {t('attach.file')}
          </button>
          <button
            type="button"
            role="menuitem"
            className={styles.pickerItem}
            onClick={() => {
              setOpen(false);
              props.onRecordVoice();
            }}
          >
            {t('attach.recordVoice')}
          </button>
          <button
            type="button"
            role="menuitem"
            className={styles.pickerItem}
            onClick={() => {
              setOpen(false);
              props.onRecordVideo();
            }}
          >
            {t('attach.recordVideo')}
          </button>
        </div>
      ) : null}

      <input
        ref={mediaInputRef}
        className={styles.hiddenInput}
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={onChosen}
      />
      <input
        ref={fileInputRef}
        className={styles.hiddenInput}
        type="file"
        multiple
        onChange={onChosen}
      />
    </div>
  );
}
